import { LocaleCode } from '@tablecheck/locales';
import { AlertAppearance } from '@tablekit/alert';
import { TOptions } from 'i18next';
import * as React from 'react';
import { TFuncKey, Trans, useTranslation } from 'react-i18next';
import { StateFrom } from 'xstate';

import { useUnlockAccountStatus } from 'pages/SignIn/components/useUnlockAccountStatus';
import { signInMachine, SignInPageController } from 'pages/SignIn/machine';
import { getRemainingTime } from 'pages/SignIn/utils';
import { FormAlert } from 'utils/form.styles';
import { parseError } from 'utils/parseError';

const getAlertProps = ({
  state,
  unlockAccountStatus
}: {
  state: StateFrom<typeof signInMachine>;
  unlockAccountStatus: 'success' | 'failed' | null;
}): {
  type: AlertAppearance;
  messageTranslation: { key: TFuncKey; options?: TOptions };
} | null => {
  let type: AlertAppearance | undefined;
  let messageTranslation: { key: TFuncKey; options?: TOptions } | undefined;
  const {
    context: { errorData, isOtpRequired }
  } = state;
  const error = errorData
    ? parseError(errorData.error, false, isOtpRequired)
    : null;
  if (unlockAccountStatus === 'success') {
    type = AlertAppearance.Success;
    messageTranslation = { key: 'forms.unlock_account_success' };
  }
  if (unlockAccountStatus === 'failed') {
    type = AlertAppearance.Danger;
    messageTranslation = { key: 'errors.unlock_account_failed' };
  }
  if (error) {
    type = AlertAppearance.Danger;
    messageTranslation = { key: error };
  }
  if (isOtpRequired) {
    type = AlertAppearance.Primary;
    messageTranslation = { key: 'errors.otp_required' };
  }
  if (error && isOtpRequired && errorData?.error !== 'otp_required') {
    type = AlertAppearance.Danger;
    messageTranslation = { key: error };
  }
  if (
    error &&
    state.matches('expiringPassword') &&
    errorData?.error === 'password_expiring'
  ) {
    type = AlertAppearance.Warning;
    messageTranslation = {
      key: error,
      options: {
        period: getRemainingTime(errorData.password_expires_at)
      }
    };
  }
  if (state.matches('unlocked.passkeyLogin.failed')) {
    type = AlertAppearance.Danger;
    messageTranslation = { key: 'errors.passkey_login_failed' };
  }
  if (state.matches('usePasskeys')) {
    if (state.matches('usePasskeys.done')) {
      type = AlertAppearance.Success;
      messageTranslation = { key: 'forms.create_passkey_success' };
    } else if (state.matches('usePasskeys.failed')) {
      type = AlertAppearance.Danger;
      messageTranslation = { key: 'forms.create_passkey_failure' };
    } else {
      type = AlertAppearance.Primary;
      messageTranslation = { key: 'forms.create_passkey_description' };
    }
  }

  return !type || !messageTranslation ? null : { type, messageTranslation };
};

const getPasskeyLink = (locale: LocaleCode) => {
  switch (locale) {
    case 'ja':
      return 'https://fidoalliance.org/passkeys/?lang=ja';
    case 'zh-CN':
    case 'zh-TW':
      return 'https://fidoalliance.org/passkeys/?lang=zh-hans';
    case 'ko':
      return 'https://fidoalliance.org/passkeys/?lang=ko';
    default:
      return 'https://fidoalliance.org/passkeys/';
  }
};

export function Alert(): JSX.Element | null {
  const [t, { language }] = useTranslation();
  const state = SignInPageController.useSelector((s) => s);
  const unlockAccountStatus = useUnlockAccountStatus();
  const alertProps = getAlertProps({
    state,
    unlockAccountStatus
  });

  return alertProps ? (
    <FormAlert
      appearance={alertProps.type}
      isShow
      shouldUsePortal={false}
      isDismissible={false}
    >
      <Trans
        t={t}
        i18nKey={alertProps.messageTranslation.key}
        tOptions={alertProps.messageTranslation.options}
        components={{
          br: <br />,
          a: (
            // eslint-disable-next-line jsx-a11y/anchor-has-content,jsx-a11y/control-has-associated-label
            <a
              href={getPasskeyLink(language as LocaleCode)}
              target="_blank"
              rel="noopener noreferrer"
            />
          )
        }}
      />
    </FormAlert>
  ) : null;
}
